import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import PasswordInput from "@/components/PasswordInput";
import { useAcenteAuth } from "@/context/AcenteAuthContext";
import { ArrowRight, KeyRound, Send } from "lucide-react";
import panelApi from "@/lib/panelApi";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

export default function AcenteLogin() {
  const nav = useNavigate();
  const { login } = useAcenteAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // Forgot password modal
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgot, setForgot] = useState({ email: "", company_name: "", phone: "", note: "" });
  const [forgotBusy, setForgotBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Giriş başarılı");
      nav("/acente");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Giriş yapılamadı");
    } finally {
      setBusy(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotBusy(true);
    try {
      await panelApi.post("/password-reset-requests", {
        email: forgot.email.trim().toLowerCase(),
        company_name: forgot.company_name.trim(),
        phone: forgot.phone.trim(),
        note: forgot.note.trim(),
      });
      toast.success("Talebiniz alındı. En kısa sürede sizinle telefondan iletişime geçilecektir.", { duration: 8000 });
      setForgotOpen(false);
      setForgot({ email: "", company_name: "", phone: "", note: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Talep gönderilemedi");
    } finally {
      setForgotBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas-cream flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md bg-white rounded-3xl border border-brand-navy/10 shadow-xl p-8" data-testid="acente-login-card">
        <div className="flex flex-col items-center text-center">
          <img
            src="/asis-logo.png"
            alt="ASIS"
            className="w-16 h-16 object-contain drop-shadow-md"
            data-testid="acente-login-asis-logo"
          />
          <div className="text-xs uppercase tracking-[0.3em] text-brand-orange font-semibold mt-3">Ariaf ASIS Acente Paneli</div>
          <h1 className="font-serif text-3xl text-brand-navy mt-1">Acente Girişi</h1>
          <p className="text-sm text-slate-600 mt-1">E-postanız ve şifrenizle oturum açın.</p>
        </div>

        <form onSubmit={submit} className="mt-6 grid gap-4" data-testid="acente-login-form">
          <div>
            <Label>E-posta</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required data-testid="acente-login-email" />
          </div>
          <div>
            <Label>Şifre</Label>
            <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="acente-login-password" />
          </div>
          <div className="flex justify-end -mt-1">
            <button
              type="button"
              onClick={() => setForgotOpen(true)}
              data-testid="acente-forgot-link"
              className="text-xs text-brand-orange hover:text-brand-orange-dark hover:underline inline-flex items-center gap-1"
            >
              <KeyRound size={11} /> Şifremi Unuttum
            </button>
          </div>
          <Button type="submit" disabled={busy} className="bg-brand-orange text-white hover:bg-brand-orange-dark" data-testid="acente-login-submit">
            {busy ? "Giriş yapılıyor..." : "Giriş yap"}
            <ArrowRight size={16} className="ml-1" />
          </Button>
        </form>

        <div className="mt-6 text-sm text-slate-600 text-center">
          Hesabınız yok mu?{" "}
          <Link to="/acente/kayit" className="text-brand-orange hover:underline font-medium" data-testid="acente-login-register-link">
            Hemen kayıt olun
          </Link>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="sm:max-w-md" data-testid="acente-forgot-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-brand-navy flex items-center gap-2">
              <KeyRound size={18} /> Şifremi Unuttum
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-600">
              Bilgilerinizi bırakın, yöneticimiz sizi telefondan arayarak şifrenizi yenileyecektir.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitForgot} className="space-y-3">
            <div>
              <Label>Şirket Adı *</Label>
              <Input
                required
                value={forgot.company_name}
                onChange={(e) => setForgot({ ...forgot, company_name: e.target.value })}
                placeholder="Örn: XYZ Umre Turizm"
                data-testid="forgot-company"
              />
            </div>
            <div>
              <Label>E-posta *</Label>
              <Input
                required
                type="email"
                value={forgot.email}
                onChange={(e) => setForgot({ ...forgot, email: e.target.value })}
                placeholder="ornek@sirket.com"
                data-testid="forgot-email"
              />
            </div>
            <div>
              <Label>Telefon</Label>
              <Input
                type="tel"
                value={forgot.phone}
                onChange={(e) => setForgot({ ...forgot, phone: e.target.value })}
                placeholder="+90 555 000 00 00"
                data-testid="forgot-phone"
              />
            </div>
            <div>
              <Label>Not (isteğe bağlı)</Label>
              <textarea
                rows={2}
                value={forgot.note}
                onChange={(e) => setForgot({ ...forgot, note: e.target.value })}
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                placeholder="Aranmak istediğiniz saat, ek not vs."
                data-testid="forgot-note"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setForgotOpen(false)}>Vazgeç</Button>
              <Button
                type="submit"
                disabled={forgotBusy}
                className="bg-brand-orange text-white hover:bg-brand-orange-dark"
                data-testid="forgot-submit"
              >
                <Send size={14} className="mr-2" /> {forgotBusy ? "Gönderiliyor..." : "Talebi Gönder"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
